import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ClaimsService } from './claims.service';
import { LocaleService } from '../locale/locale.service';
import { UserType } from '../enums/user-type';
import { IncidentType } from '../enums/incident-type';
import { InvolvedVehicleType } from '../enums/involved-vehicle-type';
import { RideReason } from '../enums/ride-reason';
import { WhoIsAtFault } from '../enums/who-is-at-fault';

@Injectable({
  providedIn: 'root'
})
export class PdfService {
  private claimsService = inject(ClaimsService);
  private localeService = inject(LocaleService);

  async generateOverviewPDF(): Promise<void> {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Add title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Claim Overview Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 30;

    // Get claim data
    const claimReport = this.claimsService.claimReport();
    const locale = this.localeService.currentTranslation;

    // Accident Description Section
    yPosition = this.addSectionHeader(pdf, 'Accident Description', yPosition);
    yPosition = this.addField(pdf, 'Your Role', this.mapUserType(), yPosition);
    yPosition = this.addField(pdf, 'License Plate', claimReport.licensePlate || 'N/A', yPosition);
    yPosition = this.addField(pdf, 'Location', this.formatAddress(claimReport.incidentAddress), yPosition);
    yPosition = this.addField(pdf, 'Date & Time', this.formatDate(claimReport.incidentDate), yPosition);
    yPosition = this.addField(pdf, 'Damage Type', this.mapDamageType(), yPosition);
    yPosition = this.addField(pdf, 'Involved Vehicles', this.mapInvolvedVehicles(), yPosition);
    yPosition = this.addField(pdf, 'Description', claimReport.detailedDescription || 'N/A', yPosition, true);

    // Check if new page needed
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 20;
    }

    // Trip Information Section
    yPosition = this.addSectionHeader(pdf, 'Trip Information', yPosition);
    yPosition = this.addField(pdf, 'Ride Reason', this.mapRideReason(), yPosition);
    yPosition = this.addField(pdf, 'Driver Age', `${claimReport.age || 'N/A'} years`, yPosition);
    yPosition = this.addField(pdf, 'Alcohol Consumption', this.mapBoolean(claimReport.hasConsumedDrugs), yPosition);
    yPosition = this.addField(pdf, 'Police Report', this.mapBoolean(claimReport.policeReportWasIssued), yPosition);
    yPosition = this.addField(pdf, 'Police Drug Test', this.mapBoolean(claimReport.policeDrugTestPerformed), yPosition);
    yPosition = this.addField(pdf, 'Witnesses Known', this.mapBoolean(claimReport.witnessesAreKnown), yPosition);
    yPosition = this.addField(pdf, 'Fault Allocation', this.mapFault(), yPosition);

    // Check if new page needed
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 20;
    }

    // Witnesses Section (if any)
    if (claimReport.witnesses && claimReport.witnesses.length > 0) {
      claimReport.witnesses.forEach((witness, index) => {
        yPosition = this.addSectionHeader(pdf, `Witness ${index + 1} Information`, yPosition);
        yPosition = this.addField(pdf, 'Surname', witness.surname || 'N/A', yPosition);
        yPosition = this.addField(pdf, 'Name', witness.name || 'N/A', yPosition);
        yPosition = this.addField(pdf, 'Phone', witness.phone || 'N/A', yPosition);
        yPosition = this.addField(pdf, 'Email', witness.email || 'N/A', yPosition);

        // Check if new page needed
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = 20;
        }
      });
    }

    // Documents Section
    yPosition = this.addSectionHeader(pdf, 'Documents', yPosition);
    yPosition = this.addField(pdf, 'Police Report Available', this.mapBoolean(claimReport.policeReportAvailable), yPosition);
    if (claimReport.policeReportAvailable && claimReport.noPoliceReportReason) {
      yPosition = this.addField(pdf, 'Police Report Reason', claimReport.noPoliceReportReason, yPosition, true);
    }

    // Check if new page needed
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 20;
    }

    // Personal Information Section
    if (claimReport.personalInformation) {
      yPosition = this.addSectionHeader(pdf, 'Personal Information', yPosition);
      yPosition = this.addField(pdf, 'Surname', claimReport.personalInformation.surname || 'N/A', yPosition);
      yPosition = this.addField(pdf, 'Name', claimReport.personalInformation.name || 'N/A', yPosition);
      yPosition = this.addField(pdf, 'Street & Number', claimReport.personalInformation.streetAndNumber || 'N/A', yPosition);
      yPosition = this.addField(pdf, 'ZIP Code', String(claimReport.personalInformation.zip || 'N/A'), yPosition);
      yPosition = this.addField(pdf, 'City', claimReport.personalInformation.city || 'N/A', yPosition);
      yPosition = this.addField(pdf, 'Phone', claimReport.personalInformation.phone || 'N/A', yPosition);
      if (claimReport.personalInformation.email) {
        yPosition = this.addField(pdf, 'Email', claimReport.personalInformation.email, yPosition);
      }
      const contactMethod = this.mapPreferredContactMethod();
      if (contactMethod) {
        yPosition = this.addField(pdf, 'Preferred Contact', contactMethod, yPosition);
      }
    }

    // Save PDF
    pdf.save('claim-overview-report.pdf');
  }

  async generateOverviewPDFFromHTML(elementId: string): Promise<void> {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Element with ID '${elementId}' not found`);
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('claim-overview-report.pdf');
  }

  private addSectionHeader(pdf: jsPDF, title: string, yPosition: number): number {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, 20, yPosition);
    return yPosition + 15;
  }

  private addField(pdf: jsPDF, label: string, value: string, yPosition: number, isMultiline = false): number {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${label}:`, 20, yPosition);

    pdf.setFont('helvetica', 'normal');
    if (isMultiline && value.length > 50) {
      const lines = pdf.splitTextToSize(value, 150);
      pdf.text(lines, 80, yPosition);
      return yPosition + (lines.length * 5) + 5;
    } else {
      pdf.text(value, 80, yPosition);
      return yPosition + 12;
    }
  }

  private mapUserType(): string {
    const userType = this.claimsService.claimReport().userType;
    /*prettier-ignore*/ console.log('>>>> _ >>>> ~ pdf.service.ts:177 ~ userType:', userType)
    const locale = this.localeService.currentTranslation;

    switch (userType) {
      case UserType.FirstParty:
        return locale.userParticipationIdentificationOptionHeadline1 || 'First Party';
      case UserType.ThirdParty:
        return locale.userParticipationIdentificationOptionHeadline2 || 'Third Party';
      default:
        return 'Unknown';
    }
  }

  private mapDamageType(): string {
    const incidentTypes = this.claimsService.claimReport().incidentTypes || [];
    const locale = this.localeService.currentTranslation;
    let result = '';

    const typeMapping = {
      [IncidentType.FailureToYield]: locale.incidentDescriptionOptionHeadline1 || 'Failure to Yield',
      [IncidentType.ParkedVehicleCollision]: locale.incidentDescriptionOptionHeadline2 || 'Parked Vehicle Collision',
      [IncidentType.NoOtherVehicleInvolved]: locale.incidentDescriptionOptionHeadline3 || 'No Other Vehicle Involved',
      [IncidentType.RearEndCollision]: locale.incidentDescriptionOptionHeadline4 || 'Rear End Collision',
      [IncidentType.OncomingTrafficCollision]: locale.incidentDescriptionOptionHeadline5 || 'Oncoming Traffic Collision',
      [IncidentType.SideSwipe]: locale.incidentDescriptionOptionHeadline6 || 'Side Swipe',
      [IncidentType.LaneChangeIncident]: locale.incidentDescriptionOptionHeadline7 || 'Lane Change Incident',
      [IncidentType.ParkingIncident]: locale.incidentDescriptionOptionHeadline8 || 'Parking Incident',
      [IncidentType.NoContact]: locale.incidentDescriptionOptionHeadline9 || 'No Contact',
      [IncidentType.TippedOverEScooter]: locale.incidentDescriptionOptionHeadline10 || 'Tipped Over E-Scooter',
      [IncidentType.Other]: locale.incidentDescriptionOptionHeadline11 || 'Other',
      [IncidentType.PersonalDamagedItem]: locale.incidentDescriptionOptionHeadline12 || 'Personal Damaged Item',
      [IncidentType.OtherPropertyDamage]: locale.incidentDescriptionOptionHeadline13 || 'Other Property Damage',
      [IncidentType.AnimalInjury]: locale.incidentDescriptionOptionHeadline14 || 'Animal Injury',
      [IncidentType.InjuredPerson]: locale.incidentDescriptionOptionHeadline15 || 'Injured Person',
      [IncidentType.InjuryWhileAvoiding]: locale.incidentDescriptionOptionHeadline16 || 'Injury While Avoiding'
    };

    incidentTypes.forEach(type => {
      if (typeMapping[type]) {
        result += typeMapping[type] + ' ';
      }
    });

    return result.trim() || 'None specified';
  }

  private mapInvolvedVehicles(): string {
    const vehicleType = this.claimsService.claimReport().vehicleType;
    const locale = this.localeService.currentTranslation;

    switch (vehicleType) {
      case InvolvedVehicleType.FourWheelsWithEngine:
        return locale.vehicleInvolvedOptionHeadline1 || 'Four Wheels With Engine';
      case InvolvedVehicleType.TwoWheelsWithEngine:
        return locale.vehicleInvolvedOptionHeadline2 || 'Two Wheels With Engine';
      case InvolvedVehicleType.TwoWheelsWithNoEngine:
        return locale.vehicleInvolvedOptionHeadline3 || 'Two Wheels With No Engine';
      case InvolvedVehicleType.None:
        return locale.vehicleInvolvedOptionHeadline4 || 'None';
      default:
        return 'Unknown';
    }
  }

  private mapRideReason(): string {
    const rideReason = this.claimsService.claimReport().rideReason;
    const locale = this.localeService.currentTranslation;

    switch (rideReason) {
      case RideReason.PrivateTrip:
        return locale.rideReasonOptionHeadline1 || 'Private Trip';
      case RideReason.BusinessTrip:
        return locale.rideReasonOptionHeadline2 || 'Business Trip';
      case RideReason.Commute:
        return locale.rideReasonOptionHeadline3 || 'Commute';
      default:
        return 'Unknown';
    }
  }

  private mapBoolean(value: boolean | null): string {
    const locale = this.localeService.currentTranslation;
    return value ? (locale.yes || 'Yes') : (locale.no || 'No');
  }

  private mapFault(): string {
    const fault = this.claimsService.claimReport().fault;
    const locale = this.localeService.currentTranslation;
    const isDriver = this.claimsService.isDriver();

    switch (fault) {
      case WhoIsAtFault.Both:
        return locale.faultAllocationOptionHeadline1 || 'Both';
      case WhoIsAtFault.ThirdParty:
        return isDriver
          ? (locale.faultAllocationOptionHeadline2 || 'Third Party')
          : (locale.thirdPartyFaultAllocationOptionHeadline2 || 'Third Party');
      case WhoIsAtFault.FirstParty:
        return isDriver
          ? (locale.faultAllocationOptionHeadline3 || 'First Party')
          : (locale.thirdPartyFaultAllocationOptionHeadline3 || 'First Party');
      case WhoIsAtFault.Unknown:
        return locale.faultAllocationOptionHeadline4 || 'Unknown';
      default:
        return 'Not specified';
    }
  }

  private mapPreferredContactMethod(): string {
    const personalInfo = this.claimsService.claimReport().personalInformation;
    if (!personalInfo) return '';

    const locale = this.localeService.currentTranslation;
    const sms = locale.sms || 'SMS';
    const email = locale.email || 'Email';
    const and = locale.and || 'and';

    if (personalInfo.contactableBySms && personalInfo.contactableByEmail) {
      return `${sms} ${and} ${email}`;
    } else if (personalInfo.contactableBySms) {
      return sms;
    } else if (personalInfo.contactableByEmail) {
      return email;
    }

    return '';
  }

  private formatAddress(address: any): string {
    if (!address) return 'N/A';

    const parts = [
      address.street,
      address.houseNumber,
      address.postcode,
      address.city
    ].filter(part => part);

    return parts.join(', ') || 'N/A';
  }

  private formatDate(date: any): string {
    if (!date) return 'N/A';

    try {
      const d = new Date(date);
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  }
}
